import * as Semantic from 'semantic-ui-react';
import { useCompositionUsage } from 'api/compositionApi';
import { Link } from 'react-router-dom';
import { VideoPlayList } from '@visikon/core-models/models/content/video-lists';
import { getTranslationFromArray } from '@visikon/core-models/typeUtils';
import { useRecoilState } from 'recoil';
import { languageState } from 'redux-recoil-sync.state';
import { useMemo } from 'react';

interface Props {
  compositionId: string;
  onClose(): void;
}

export const CompositionUsageModal = ({ compositionId, onClose }: Props) => {
  const usageQuery = useCompositionUsage(compositionId);
  const { isLoading, data, isError, error } = usageQuery;
  const playlists = data?.playlists || [];

  return (
    <Semantic.Modal open onClose={onClose}>
      <Semantic.Modal.Header>Composition Usage</Semantic.Modal.Header>
      <Semantic.Modal.Content>
        {isLoading && (
          <Semantic.Loader inline="centered" active>
            Loading usage...
          </Semantic.Loader>
        )}
        {isError && (
          <Semantic.Message error>
            <Semantic.Message.Header>Failed to load usage information</Semantic.Message.Header>
            <p>{error instanceof Error ? error.message : 'An error occurred while fetching composition usage. Please try again.'}</p>
          </Semantic.Message>
        )}
        {!isLoading && !isError && playlists.length === 0 && (
          <Semantic.Message info>This composition is not used in any video playlists.</Semantic.Message>
        )}
        {!isLoading && !isError && playlists.length > 0 && (
          <>
            <p>This composition is used in {playlists.length} video playlist(s):</p>
            <Semantic.List divided relaxed>
              {playlists.map((playlist) => (
                <PlaylistListItem key={playlist._id} playlist={playlist} />
              ))}
            </Semantic.List>
          </>
        )}
      </Semantic.Modal.Content>

      <Semantic.Modal.Actions>
        <Semantic.Button color="black" onClick={onClose}>
          Close
        </Semantic.Button>
      </Semantic.Modal.Actions>
    </Semantic.Modal>
  );
};

export function PlaylistListItem({ playlist }: { playlist: VideoPlayList }) {
  const [language] = useRecoilState(languageState);
  const translated = useMemo(
    () => getTranslationFromArray(playlist.translations, language, (t) => !!t.data?.name),
    [language, playlist.translations],
  );

  return (
    <Semantic.List.Item key={playlist._id}>
      <Semantic.List.Icon name="video" size="large" verticalAlign="middle" />
      <Semantic.List.Content>
        <Semantic.List.Header as={Link} to={`/content/videoList/${playlist._id}`}>
          {translated?.data?.name || 'Untitled Playlist'}
        </Semantic.List.Header>
      </Semantic.List.Content>
    </Semantic.List.Item>
  );
}
